import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'
import * as z from 'zod'

import { FormattedMessage, useIntl } from 'react-intl'

import { formsMessages } from '@/lib/core/messages'
import { Button } from '@/lib/core/components/Button'
import { SelectField } from '@/lib/core/components/Select'
import { TextField } from '@/lib/core/components/TextField'

import { useAddPTU } from '@/lib/features/Assets/api'
import { useTrackingSourceOptions } from '@/lib/features/Assets/components/AddAssetModal/hooks/useTrackingSourceOptions'
import {
  AddAssetPTUPayload,
  AddAssetPTUResponse,
  PTUResponseError,
} from '@/lib/features/Assets/interfaces'
import {
  ErrorBox,
  FormActions,
  FormGrid,
} from '@/lib/features/Assets/components/AddAssetModal/AddAssetModal.styles'
import { PTUFormState } from '@/lib/features/Assets/components/AddAssetModal/interfaces'
import messages from '@/lib/features/Assets/components/AddAssetModal/AddAssetModal.messages'

import { setFormErrors } from '@/lib/utils/forms'

export type PTUFormProps = {
  onSent?: (response: AddAssetPTUResponse) => void
}
const schema = z.object({
  description: z
    .string({ required_error: formsMessages.requiredValidation.defaultMessage })
    .nullable(),
  trackingIdentifier: z
    .string({ required_error: formsMessages.requiredValidation.defaultMessage })
    .nullable(),
  trackingSourceId: z
    .string({ required_error: formsMessages.requiredValidation.defaultMessage })
    .nullable(),
})

export const PTUForm = ({ onSent }: PTUFormProps) => {
  const { formatMessage } = useIntl()
  const trackingOptions = useTrackingSourceOptions()
  const { trigger: addAssetPTU } = useAddPTU()

  const formMethods = useForm<PTUFormState>({
    defaultValues: {
      description: null,
      trackingIdentifier: null,
      trackingSourceId: null,
    },
    resolver: zodResolver(schema),
  })

  const { formState, handleSubmit, setError } = formMethods
  const { isSubmitted, isSubmitting, isValid } = formState

  const onSubmit = async (values: PTUFormState) => {
    try {
      const response = await addAssetPTU(values as AddAssetPTUPayload)

      if (onSent) {
        onSent(response)
      }
    } catch (err) {
      const errors = err as PTUResponseError

      if (errors) {
        setFormErrors(setError, errors)
      }
    }
  }

  return (
    <FormProvider {...formMethods}>
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <FormGrid>
          <TextField
            id="add-asset-form-description"
            name="description"
            type="text"
            displayInline
            required
            disabled={isSubmitting}
            label={formatMessage(messages.description)}
          />
          <SelectField
            aria-labelledby="trackingSourceId"
            name="trackingSourceId"
            label={formatMessage(messages.source)}
            placeholder={formatMessage(messages.select)}
            displayInline
            required
            disabled={isSubmitting}
            selectProps={{
              isDisabled: isSubmitting,
              isSearchable: false,
              maxMenuHeight: 185,
              menuPlacement: 'bottom',
              options: trackingOptions,
            }}
          />
          <TextField
            id="add-asset-form-trackingIdentifier"
            type="text"
            name="trackingIdentifier"
            displayInline
            required
            disabled={isSubmitting}
            label={formatMessage(messages.trackId)}
          />
        </FormGrid>
        {isSubmitted && !isValid && (
          <ErrorBox>
            <FormattedMessage {...messages.customErrorCommunicate} />
          </ErrorBox>
        )}
        <FormActions>
          <Button
            isLoading={isSubmitting}
            size="large"
            variant="secondary"
            $fitToContent
            minWidth
            type="submit"
          >
            <FormattedMessage {...messages.submit} />
          </Button>
        </FormActions>
      </form>
    </FormProvider>
  )
}
